/** Browser close/reload protection mirrors the existing form dirty state; App Bridge owns native leave dialogs. */
import { useCallback, useEffect, useRef } from 'react'
import { useAppBridge } from '@shopify/app-bridge-react'
import { shopifyComponentCopy } from '@testfeed/product-language'
import { useBuilderEvaluationInput } from './useBuilderEvaluationWork'
import { reportClientDiagnostic } from '../../clientDiagnostics'

export const STUDY_BUILDER_SAVE_BAR_ID = 'study-builder-save-bar'

export function useStudyBuilderSaveBar(isDirty: boolean, readDirty: () => boolean) {
  const shopify = useAppBridge()
  const ref = useRef<UISaveBarElement | null>(null)
  const latest = useBuilderEvaluationInput({ readDirty, shopify })
  // A late native confirmation may hide the bar after a newer edit. Re-project current form state.
  const onHide = useCallback(() => {
    const bar = ref.current
    if (!bar?.isConnected || !latest.current.readDirty()) return
    void bar.show().catch((cause) => {
      if (!bar.isConnected) return
      console.error('[shopify-save-bar] Restore failed', cause)
      try {
        latest.current.shopify.toast?.show(shopifyComponentCopy.studyBuilderPage.couldNotRestoreSaveBar, {
          isError: true,
        })
      } catch (toastFailure) {
        console.error('[shopify-save-bar] Restore notice unavailable', toastFailure)
      }
      void reportClientDiagnostic(latest.current.shopify, {
        kind: 'error',
        schema: 'shopify-navigation',
        paths: ['save_bar_restore_failed'],
        code: 'SHOPIFY_SAVE_BAR_RESTORE_FAILED',
      })
    })
  }, [latest])
  useEffect(() => {
    if (!isDirty) return
    const protectUnsavedWork = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }
    window.addEventListener('beforeunload', protectUnsavedWork)
    return () => window.removeEventListener('beforeunload', protectUnsavedWork)
  }, [isDirty])
  return { ref, onHide }
}
